import React from 'react';

/*
 * A trend arrow is only rendered when the caller actually passes `trend` —
 * no more fabricated "+12%" deltas with no baseline. `hint` is for a
 * grounded, non-comparative supporting line instead (e.g. "1 unresolved").
 */

interface StatCardProps {
  label: string;
  value: string | number;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  tone?: 'blue' | 'emerald' | 'purple' | 'amber' | 'red' | 'slate';
  hint?: string;
  trend?: { value: number; isPositive: boolean; label?: string };
  onClick?: () => void;
}

const TONE_STYLES: Record<NonNullable<StatCardProps['tone']>, string> = {
  blue: 'bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300',
  emerald: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  purple: 'bg-purple-50 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  amber: 'bg-accent-50 text-accent-800 dark:bg-accent-900/40 dark:text-accent-300',
  red: 'bg-red-50 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  slate: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
};

const StatCard: React.FC<StatCardProps> = ({ label, value, icon: Icon, tone = 'slate', hint, trend, onClick }) => {
  const Wrapper = onClick ? 'button' : 'div';
  return (
    <Wrapper
      onClick={onClick}
      className={`flex items-start justify-between rounded-sm border border-gray-300 bg-white p-5 dark:border-gray-700 dark:bg-gray-800 ${
        onClick ? 'text-left transition-colors hover:border-primary-500' : ''
      }`}
    >
      <div>
        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}</p>
        <p className="mt-1.5 text-2xl font-semibold text-primary-900 dark:text-primary-200">{value}</p>
        {hint && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{hint}</p>}
        {trend && (
          <p className={`mt-1 text-xs font-medium ${trend.isPositive ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
            {trend.isPositive ? '▲' : '▼'} {Math.abs(trend.value)}%{trend.label ? ` ${trend.label}` : ''}
          </p>
        )}
      </div>
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-sm ${TONE_STYLES[tone]}`}>
        <Icon className="h-5 w-5" />
      </div>
    </Wrapper>
  );
};

export default StatCard;
